import { useState } from "react";
import { toast } from "react-toastify";
import {
  doctorGetTemplate,
  doctorSaveTemplate,
  doctorUpdateTemplate,
} from "../../config/service";

import { authenticatedRequest } from "../../config/axios";

/**
 * Custom hook to manage template operations (add, edit, delete, search).
 *
 * @param {Array} initialFieldArray - The list of field options (value + label)
 * @param {Function} t - Translation function from react-i18next
 */
const useTemplateManager = (initialFieldArray, t) => {
  // ---------------- STATE ----------------
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [addTemplate, setAddTemplate] = useState({
    templateName: "",
    templateText: "",
    templateField: [],
  });
  const [templateList, setTemplateList] = useState([]);

  const [isEditTemp, setIsEditTemp] = useState(false);
  const [templateId, setTemplateId] = useState("");

  const [deleteTempModal, setDeleteTempModal] = useState(false);

  const [showSearch, setShowSearch] = useState(false);
  const [filteredTemplate, setFilteredTemplate] = useState([]);
  const [targetInput, setTargetInput] = useState("");

  // ---------------- HANDLERS ----------------

  const handleTemplateChange = (event) => {
    console.log("event", event);
    const { name, value } = event.target;
    setAddTemplate((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiChange = (value) => {
    setAddTemplate((prev) => ({ ...prev, templateField: value }));
  };

  // ---------------- VALIDATION ----------------

  const templateValidation = () => {
    if (!addTemplate.templateName) {
      toast.warning(t("Please enter template name"));
      return false;
    }
    if (!addTemplate.templateText) {
      toast.warning(t("Please enter template text"));
      return false;
    }
    if (addTemplate.templateField.length === 0) {
      toast.warning(t("Please select field name"));
      return false;
    }
    return true;
  };

  // ---------------- API CALLS ----------------

  const getTemplateList = async () => {
    try {
      const response = await doctorGetTemplate();
      if (response.data.code === 200) {
        setTemplateList(response.data.templates);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleTemplateSubmit = async () => {
    const templateFieldArray = addTemplate.templateField.map((item) => item.value);

    const payload = {
      templateName: addTemplate.templateName,
      templateText: addTemplate.templateText,
      templateField: templateFieldArray,
    };

    if (!templateValidation()) return;

    try {
      const response = await doctorSaveTemplate(payload);
      const { code, message } = response.data;

      if (code === 201) {
        toast.success(t(message));
        setShowTemplateModal(false);
        getTemplateList();

        // reset
        setAddTemplate({ templateName: "", templateText: "", templateField: [] });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const submitEditTemplate = async () => {
    const templateFieldArray = addTemplate.templateField.map((item) => item.value);

    const payload = {
      templateId,
      templateName: addTemplate.templateName,
      templateText: addTemplate.templateText,
      templateField: templateFieldArray,
    };

    if (!templateValidation()) return;

    try {
      const response = await doctorUpdateTemplate(payload);
      const { code, message } = response.data;

      if (code === 201) {
        toast.success(t(message));
        setIsEditTemp(false);
        getTemplateList();

        // Reset
        setTemplateId("");
        setAddTemplate({ templateName: "", templateText: "", templateField: [] });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      const response = await authenticatedRequest({
        method: "delete",
        url: `/doctor/deleteTemplate/${templateId}`,
        headers: { "Content-Type": "application/json" },
      });

      const { code, message } = response.data;

      if (code === 201) {
        toast.success(t(message));
        setDeleteTempModal(false);
        getTemplateList();
      }
    } catch (error) {
      console.log(error);
    }
  };

  // ---------------- EDIT TEMPLATE ----------------

  const handleEditTemplate = (data) => {
    setIsEditTemp(true);
    setTemplateId(data._id);

    const valuesArray = data.templateField;

    const formattedFields = valuesArray.map((value) => {
      const match = initialFieldArray.find((item) => item.value === value);
      return match ? { value, label: match.label } : { value, label: value };
    });

    setAddTemplate({
      templateName: data.templateName,
      templateText: data.templateText,
      templateField: formattedFields,
    });
  };

  // ---------------- SEARCH ----------------

  const handleFilteredTemp = (key) => {
    if (!key) return [];

    return templateList.filter((item) => item.templateField?.includes(key));
  };

  // ---------------- RESET HANDLERS ----------------

  const handleAddTemplate = () => {
    setIsEditTemp(false);
    setTemplateId("");
    setAddTemplate({ templateName: "", templateText: "", templateField: [] });
  };

  const handleCloseTempModal = () => {
    setIsEditTemp(false);
    setTemplateId("");
    setShowTemplateModal(false);
    setAddTemplate({ templateName: "", templateText: "", templateField: [] });
  };

  const handleCloseDeleteTemplate = () => {
    if (!isEditTemp) setTemplateId("");
    setDeleteTempModal(false);
  };

  // ---------------- RETURN OBJECT ----------------

  return {
    // state
    showTemplateModal,
    setShowTemplateModal,
    addTemplate,
    templateList,
    isEditTemp,
    templateId,
    deleteTempModal,
    showSearch,
    setShowSearch,
    filteredTemplate,
    setFilteredTemplate,
    targetInput,
    setTargetInput,
    setIsEditTemp,
    setAddTemplate,

    // handlers
    handleTemplateChange,
    handleMultiChange,
    handleTemplateSubmit,
    handleEditTemplate,
    handleFilteredTemp,
    handleAddTemplate,
    submitEditTemplate,
    handleCloseTempModal,
    handleDeleteTemplate,
    handleCloseDeleteTemplate,
    setDeleteTempModal,
    setTemplateId,

    // API
    getTemplateList,
  };
};

export default useTemplateManager;
